import React, { useState, useEffect, useCallback, ReactElement } from 'react';
import {
  Autocomplete,
  Box,
  TextField,
  Typography,
  CircularProgress,
  InputAdornment,
  Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { generateDataTestId } from '../../../../utils';
import { useTenant } from '../../../../contexts/TenantContext';
import { EnrichedAutocompleteResult } from '../types';
import debounce from 'lodash/debounce';

interface TaxIdSearchFieldProps {
  inputValue: string;
  selectedCounty: string | null;
  isLoading: boolean;
  locationType: string;
  noTaxRecordModeRef: React.MutableRefObject<boolean>;
  taxOptions: EnrichedAutocompleteResult[];
  selectedPrediction: EnrichedAutocompleteResult | null;
  onInputChange: (value: string) => void;
  onCountyChange: (county: string | null) => void;
  onSearch: (taxId: string, county: string) => void;
  handleAddressChange: (
    event: React.SyntheticEvent,
    value: EnrichedAutocompleteResult | null,
  ) => void;
  formatAddress: (result: EnrichedAutocompleteResult) => string;
  compareEnrichedResults: (
    option: EnrichedAutocompleteResult,
    value: EnrichedAutocompleteResult,
  ) => boolean;
}

/**
 * TaxIdSearchField component - handles Tax ID search with county selection
 *
 * This component provides county selection and tax ID input for Realist subscribers
 * Only triggers search when both county is selected and tax ID meets minimum length
 */
const TaxIdSearchField: React.FC<TaxIdSearchFieldProps> = ({
  inputValue,
  selectedCounty,
  isLoading,
  locationType,
  noTaxRecordModeRef,
  taxOptions,
  selectedPrediction,
  onInputChange,
  onCountyChange,
  onSearch,
  handleAddressChange,
  formatAddress,
  compareEnrichedResults,
}) => {
  const { getConfigValue } = useTenant();
  const [availableCounties, setAvailableCounties] = useState<string[]>([]);
  const TAX_ID_MIN_LENGTH = 3; // Minimum characters for tax ID search

  useEffect(() => {
    // Parse the PUBLIC_RECORD_COUNTIES config value
    try {
      const countiesConfig = getConfigValue<string>(
        'PUBLIC_RECORD_COUNTIES',
        '[]',
      );
      const counties = JSON.parse(countiesConfig);
      setAvailableCounties(counties);
    } catch (error) {
      console.error('Error parsing PUBLIC_RECORD_COUNTIES:', error);
      setAvailableCounties([]);
    }
  }, [getConfigValue]);

  // Create a debounced search function
  const debouncedSearch = useCallback(
    debounce((taxId: string, county: string) => {
      if (county && taxId.trim().length >= TAX_ID_MIN_LENGTH) {
        onSearch(taxId, county);
      }
    }, 500),
    [onSearch],
  );

  useEffect(() => {
    // Trigger search when both county and tax ID are valid
    if (selectedCounty && inputValue.trim().length >= TAX_ID_MIN_LENGTH) {
      debouncedSearch(inputValue, selectedCounty);
    }
  }, [inputValue, selectedCounty, debouncedSearch]);

  const isDisabled = locationType !== 'taxId' || noTaxRecordModeRef.current;

  return (
    <Box
      pr={{
        xl: 20,
      }}
    >
      <Typography
        fontWeight="bold"
        mt={{
          md: 3,
        }}
        sx={{ color: isDisabled ? 'text.disabled' : null }}
        mb={-1}
      >
        Select County
      </Typography>
      <Autocomplete
        data-testid={generateDataTestId('Select County', undefined, 'dropdown')}
        fullWidth
        options={availableCounties}
        value={selectedCounty}
        onChange={(event, newValue) => onCountyChange(newValue)}
        disabled={isDisabled}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Choose a county"
            variant="outlined"
            sx={{ mt: 2 }}
          />
        )}
        noOptionsText="No counties available"
      />

      <Typography
        fontWeight="bold"
        mt={{
          md: 3,
        }}
        sx={{ color: isDisabled ? 'text.disabled' : null }}
        mb={-1}
      >
        Enter Tax ID
      </Typography>
      <Autocomplete
        data-testid={generateDataTestId('Enter Tax ID', undefined, 'textField')}
        fullWidth
        options={taxOptions}
        inputValue={inputValue}
        getOptionLabel={(option) => formatAddress(option)}
        isOptionEqualToValue={compareEnrichedResults}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={
              selectedCounty ? 'Enter Tax ID' : 'Please select a county first'
            }
            variant="outlined"
            sx={{
              mt: 2,
              '& .MuiOutlinedInput-root': {
                '&.Mui-disabled': {
                  backgroundColor: 'background.default',
                  '& input': {
                    color: 'text.disabled',
                  },
                  '& fieldset': {
                    borderColor: 'grey.300',
                  },
                },
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {isLoading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        readOnly={isDisabled || !selectedCounty}
        disabled={isDisabled || !selectedCounty}
        value={selectedPrediction}
        onChange={handleAddressChange}
        onInputChange={(event, newInputValue) => {
          onInputChange(newInputValue);
        }}
        renderOption={(props, option) => {
          const { key, ...otherProps } = props;
          return (
            <li key={key} {...otherProps}>
              <Grid container>
                <Grid size={12}>
                  <Typography variant="body1">
                    {formatAddress(option)}
                  </Typography>
                </Grid>
                {option.enrichedData && (
                  <Grid container>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {option.enrichedData.TaxRecord &&
                      option.enrichedData.TaxRecord.TaxId
                        ? `Tax ID: ${option.enrichedData.TaxRecord.TaxId} | `
                        : ''}
                      {option.enrichedData.Listings.length} Listing
                      {option.enrichedData.Listings.length !== 1 ? 's' : ''}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </li>
          );
        }}
        noOptionsText={
          isLoading
            ? 'Loading...'
            : inputValue.length < TAX_ID_MIN_LENGTH
              ? 'Enter at least 3 characters'
              : 'No tax records found'
        }
        filterOptions={(x) => x} // Disable built-in filtering
      />
    </Box>
  );
};

export default TaxIdSearchField;
